import React, { useEffect, useMemo, useRef, useState } from 'react';
import AppLayout from 'components/ui/AppLayout';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import LeadsStats from './components/LeadsStats';
import LeadsFilterPanel from './components/LeadsFilterPanel';
import LeadsTable from './components/LeadsTable';
import LeadsFormModal from './components/LeadsFormModal';
import { leadsApi } from 'api/leads';
import { importsApi } from 'api/imports';
import { reportsApi } from 'api/reports';
import { getCurrentUser } from 'api/client';
import { useToast } from 'components/ui/Toast';
import { inrCompact } from 'utils/inr';
import * as XLSX from 'xlsx';

export default function Leads() {
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const currentUser = getCurrentUser();
  const isManager = currentUser?.role === 'Owner' || currentUser?.role === 'Manager';
  const [isExporting, setIsExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ status: 'all', source: 'all', rep: 'all' });
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formError, setFormError] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [importState, setImportState] = useState({ type: '', message: '' });
  const [isImporting, setIsImporting] = useState(false);
  const [batch, setBatch] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const fileInputRef = useRef(null);

  const loadLeadsData = async () => {
    const [statsResponse, leadsResponse] = await Promise.all([
      leadsApi.getStats(),
      leadsApi.getAll(),
    ]);

    return {
      statsResponse,
      leadsResponse: leadsResponse || [],
    };
  };

  const statusToEnum = {
    New: 1,
    Contacted: 2,
    Qualified: 3,
    Unqualified: 4,
    Won: 5,
    Lost: 6,
  };

  const buildLeadPayload = (lead) => ({
    fullName: lead?.name?.trim(),
    email: lead?.email?.trim(),
    phone: lead?.phone?.trim() || null,
    company: lead?.company?.trim(),
    source: lead?.source || 'Website',
    status: typeof lead?.status === 'number' ? lead.status : (statusToEnum[lead?.status] || 1),
    estimatedValue: Number(lead?.value || 0),
    assignedTo: lead?.rep || 'Unassigned',
    notes: [lead?.profession, lead?.experience].filter(Boolean).join(' | ') || null,
  });

  const pollBatch = async (batchId) => {
    for (let attempt = 0; attempt < 40; attempt += 1) {
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const current = await importsApi.get(batchId);
        setBatch((prev) => (prev && prev.id === batchId ? current : prev));
        if (['done', 'partial', 'failed', 'undone'].includes(current.status)) {
          if (current.status === 'done') {
            const data = await loadLeadsData();
            setStats(data.statsResponse);
            setLeads(data.leadsResponse);
          }
          return;
        }
      } catch {
        return;
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const data = await loadLeadsData();

        if (!isMounted) {
          return;
        }

        setStats(data.statsResponse);
        setLeads(data.leadsResponse);
      } catch (error) {
        if (isMounted) {
          setLoadError(error?.message || 'Unable to load leads.');
          setStats(null);
          setLeads([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleLeadSubmit = async (formData) => {
    const payload = buildLeadPayload(formData);
    setFormError("");
    try {
      if (selectedLead?.id) {
        await leadsApi.update(selectedLead.id, payload);
        toast.success('Lead updated.');
      } else {
        await leadsApi.create(payload);
        toast.success('Lead created.');
      }
    } catch (error) {
      // Sprint 15 QA: duplicate email/phone (409) and validation errors (400)
      // must surface instead of failing silently.
      setFormError(error?.message || "Could not save the lead. Please try again.");
      return;
    }

    const data = await loadLeadsData();
    setStats(data.statsResponse);
    setLeads(data.leadsResponse);
    setIsFormOpen(false);
    setSelectedLead(null);
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await leadsApi.remove(leadId);
      toast.success('Lead deleted.');
      const data = await loadLeadsData();
      setStats(data.statsResponse);
      setLeads(data.leadsResponse);
    } catch (error) {
      toast.error(error?.message || 'Could not delete the lead.');
    }
  };

  const handleStatusUpdate = async (leadId, newStatus) => {
    const current = leads.find((lead) => lead.id === leadId);
    if (!current) {
      return;
    }

    const payload = buildLeadPayload({
      ...current,
      status: newStatus,
      name: current.fullName || current.name,
      rep: current.assignedTo || current.rep,
      value: current.estimatedValue || current.value,
    });

    try {
      await leadsApi.update(leadId, payload);
      const data = await loadLeadsData();
      setStats(data.statsResponse);
      setLeads(data.leadsResponse);
    } catch (error) {
      toast.error(error?.message || 'Could not update the lead status.');
    }
  };

  const handleImportClick = () => {
    setImportState({ type: '', message: '' });
    fileInputRef.current?.click();
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      await reportsApi.exportDataset('leads');
    } catch (error) {
      setImportState({ type: 'error', message: error?.message || 'Export failed.' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileImport = async (event) => {
    const selectedFile = event?.target?.files?.[0];
    if (!selectedFile) {
      return;
    }

    setIsImporting(true);
    setImportState({ type: '', message: '' });
    setBatch(null);

    try {
      let csv;
      if (/\.csv$/i.test(selectedFile.name)) {
        csv = await selectedFile.text();
      } else {
        const buffer = await selectedFile.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheetName = workbook.SheetNames?.[0];
        const worksheet = workbook.Sheets?.[sheetName];

        if (!worksheet) {
          throw new Error('Could not read any worksheet from the selected file.');
        }

        const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        if (!rows.length) {
          throw new Error('The selected sheet has no data rows.');
        }
        csv = XLSX.utils.sheet_to_csv(worksheet);
      }

      const file = new File([csv], `import-${Date.now()}.csv`, { type: 'text/csv' });
      const created = await importsApi.upload(file);
      setBatch({ id: created.id, status: created.status });
      setImportState({
        type: 'success',
        message: `Queued ${created.totalRows} rows for server-side import.`,
      });
      pollBatch(created.id);
    } catch (error) {
      setImportState({
        type: 'error',
        message: error?.message || 'Import failed. Please check your file and try again.',
      });
    } finally {
      setIsImporting(false);
      if (event?.target) {
        event.target.value = '';
      }
    }
  };

  const handleUndoBatch = async (batchId) => {
    setUndoing(true);
    try {
      await importsApi.undo(batchId);
      pollBatch(batchId);
    } catch (error) {
      setImportState({
        type: 'error',
        message: error?.message || 'Undo failed. Please try again.',
      });
    } finally {
      setUndoing(false);
    }
  };

  const batchBannerColors = {
    uploaded: { border: '#93C5FD', bg: '#EFF6FF', color: '#1E40AF' },
    processing: { border: '#93C5FD', bg: '#EFF6FF', color: '#1E40AF' },
    done: { border: '#86EFAC', bg: '#F0FDF4', color: '#166534' },
    partial: { border: '#FCD34D', bg: '#FFFBEB', color: '#92400E' },
    failed: { border: '#FCA5A5', bg: '#FEF2F2', color: '#991B1B' },
    undone: { border: '#CBD5E1', bg: '#F8FAFC', color: '#475569' },
  };

  const statsCards = useMemo(() => {
    if (!stats) {
      return undefined;
    }

    return [
      { label: 'Total Leads', value: String(stats.totalLeads), change: null, icon: 'Users', color: '#3B82F6', bg: '#DBEAFE' },
      { label: 'Qualified', value: String(stats.qualifiedLeads), change: null, icon: 'UserCheck', color: '#15803D', bg: '#DCFCE7' },
      { label: 'Pipeline Value', value: inrCompact(stats.pipelineValue), change: null, icon: 'IndianRupee', color: '#7C3AED', bg: '#EDE9FE' },
      { label: 'Conversion Rate', value: `${stats.conversionRatePercent}%`, change: null, icon: 'TrendingUp', color: '#D97706', bg: '#FEF3C7' },
    ];
  }, [stats]);

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1
              className="font-heading font-bold text-2xl md:text-3xl"
              style={{ color: 'var(--color-foreground)', letterSpacing: '-0.02em' }}
            >
              Leads
            </h1>
            <p className="font-caption text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              Track, qualify, and convert your leads into opportunities
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isManager && (
              <>
                <Button variant="outline" size="sm" iconName="Download" iconPosition="left" iconSize={14} onClick={handleExportCsv} loading={isExporting}>
                  Export
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Upload"
                  iconPosition="left"
                  iconSize={14}
                  onClick={handleImportClick}
                  loading={isImporting}
                >
                  Import Excel
                </Button>
              </>
            )}
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              iconSize={14}
              onClick={() => {
                setSelectedLead(null);
                setIsFormOpen(true);
              }}
            >
              Add Lead
            </Button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={handleFileImport}
        />

        {formError && (
          <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: 'var(--color-error)', background: 'var(--color-card)' }}>
            <p className="font-caption text-sm" style={{ color: 'var(--color-error)' }}>{formError}</p>
          </div>
        )}

        {importState?.message && (
          <div
            className="mb-4 px-4 py-3 rounded-lg border text-sm font-caption"
            style={{
              borderColor:
                importState.type === 'success'
                  ? '#86EFAC'
                  : importState.type === 'warning'
                    ? '#FCD34D'
                    : '#FCA5A5',
              background:
                importState.type === 'success'
                  ? '#F0FDF4'
                  : importState.type === 'warning'
                    ? '#FFFBEB'
                    : '#FEF2F2',
              color:
                importState.type === 'success'
                  ? '#166534'
                  : importState.type === 'warning'
                    ? '#92400E'
                    : '#991B1B',
            }}
          >
            {importState.message}
          </div>
        )}

        {batch && (
          <div
            className="mb-4 px-4 py-3 rounded-lg border text-sm font-caption flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
            style={{
              borderColor: batchBannerColors[batch.status]?.border || '#CBD5E1',
              background: batchBannerColors[batch.status]?.bg || '#F8FAFC',
              color: batchBannerColors[batch.status]?.color || '#475569',
            }}
          >
            <div className="flex-1">
              {batch.status === 'uploaded' || batch.status === 'processing' ? (
                <span>
                  Importing <b>{batch.totalRows}</b> rows&hellip;{' '}
                  {batch.processedRows > 0 && `(${batch.processedRows} processed)`}
                </span>
              ) : batch.status === 'undone' ? (
                <span>Import {batch.fileName || 'batch'} undone. Created leads were removed.</span>
              ) : batch.status === 'failed' ? (
                <span>Import <b>{batch.fileName || 'batch'}</b> failed. No leads were created.</span>
              ) : (
                <span>
                  Import done: <b>{batch.createdRows}</b> created, <b>{batch.updatedRows}</b> updated,{' '}
                  <b>{batch.skippedRows}</b> skipped, <b>{batch.failedRows}</b> failed.
                </span>
              )}
            </div>
            {(batch.status === 'done' || batch.status === 'partial') && (
              <button
                onClick={() => handleUndoBatch(batch.id)}
                disabled={undoing}
                className="font-caption text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors disabled:opacity-50"
                style={{
                  borderColor: '#FCA5A5',
                  background: '#FEF2F2',
                  color: '#991B1B',
                }}
              >
                {undoing ? 'Undoing...' : 'Undo Import'}
              </button>
            )}
          </div>
        )}

        {loadError && (
          <div className="mb-4 rounded-lg border px-4 py-3 flex items-center justify-between gap-4" style={{ borderColor: 'var(--color-error)', background: 'var(--color-card)' }}>
            <p className="font-caption text-sm" style={{ color: 'var(--color-error)' }}>{loadError}</p>
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              iconSize={14}
              onClick={async () => {
                setIsLoading(true);
                setLoadError('');
                try {
                  const data = await loadLeadsData();
                  setStats(data.statsResponse);
                  setLeads(data.leadsResponse);
                } catch (error) {
                  setLoadError(error?.message || 'Unable to load leads.');
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              Retry
            </Button>
          </div>
        )}

        {/* Stats */}
        <div className="mb-6">
          <LeadsStats stats={statsCards} />
        </div>

        {/* Search + Filter bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Icon
              name="Search"
              size={16}
              color="var(--color-text-secondary)"
              className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search leads by name, company, email, phone, profession..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-lg border font-caption text-sm transition-colors"
              style={{
                borderColor: 'var(--color-border)',
                background: 'var(--color-card)',
                color: 'var(--color-foreground)',
                outline: 'none',
              }}
              onFocus={(e) => (e.target.style.borderColor = 'var(--color-primary)')}
              onBlur={(e) => (e.target.style.borderColor = 'var(--color-border)')}
            />
            {searchQuery && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setSearchQuery('')}
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <Icon name="X" size={14} color="currentColor" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters((p) => !p)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border font-caption text-sm font-medium transition-all duration-150"
            style={{
              background: showFilters ? 'var(--color-primary)' : 'var(--color-card)',
              color: showFilters ? '#fff' : 'var(--color-foreground)',
              borderColor: showFilters ? 'var(--color-primary)' : 'var(--color-border)',
            }}
          >
            <Icon name="SlidersHorizontal" size={15} color={showFilters ? '#fff' : 'var(--color-text-secondary)'} />
            Filters
            {(filters?.status !== 'all' || filters?.source !== 'all' || filters?.rep !== 'all') && (
              <span className="w-2 h-2 rounded-full bg-amber-400" />
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mb-4">
            <LeadsFilterPanel
              filters={filters}
              onFiltersChange={setFilters}
              onClose={() => setShowFilters(false)}
            />
          </div>
        )}

        {/* Table */}
        {isLoading ? (
          <div className="rounded-xl border px-4 py-16 text-center" style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)' }}>
            <p className="font-caption text-sm" style={{ color: 'var(--color-text-secondary)' }}>Loading leads...</p>
          </div>
        ) : (
        <LeadsTable
          searchQuery={searchQuery}
          filters={filters}
          initialLeads={leads}
          onEditLead={(lead) => {
            setSelectedLead(lead);
            setIsFormOpen(true);
          }}
          onDeleteLead={handleDeleteLead}
          onStatusChange={handleStatusUpdate}
          canDelete={isManager}
          onAddNew={() => {
            setSelectedLead(null);
            setIsFormOpen(true);
          }}
        />
        )}

        <LeadsFormModal
          isOpen={isFormOpen}
          lead={selectedLead}
          onClose={() => {
            setIsFormOpen(false);
            setSelectedLead(null);
            setFormError('');
          }}
          onSubmit={handleLeadSubmit}
        />

        {/* Footer */}
        <div
          className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 mt-4 border-t"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <p className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            &copy; {new Date()?.getFullYear()} CRMAnalytics. All rights reserved.
          </p>
          <div className="flex items-center gap-1.5">
            <Icon name="Shield" size={12} color="var(--color-success)" />
            <span className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>SSL Secured</span>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
